import {Fragment, useState} from 'react'

import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Chip from '@mui/material/Chip'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreIcon from '@mui/icons-material/MoreVert'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UnverifiedIcon from '@mui/icons-material/MarkEmailUnreadOutlined'
import VerifiedIcon from '@mui/icons-material/MarkEmailReadOutlined'
import PropTypes from 'prop-types'

import Modal from '../../shared/Modal'
import VerifyEmail from './VerifyEmail'

import {
  PROMOTE,
  VERIFY,
  emailAction,
  emailChip,
  emailDetail,
  isRemovalRefused,
  isVerified,
  promotionPrompt,
  removalPrompt
} from '../../../lib/emails'

const REMOVE = 'remove'

const EmailAddress = props => {
  const {email, emails} = props
  const [anchorEl, setAnchorEl] = useState(null)
  const [confirming, setConfirming] = useState('')
  const verified = isVerified(email)
  const chip = emailChip(email)
  const action = emailAction(email)
  const prompt = removalPrompt({email, emails})
  const promotion = promotionPrompt(emails)
  const AddressIcon = verified ? VerifiedIcon : UnverifiedIcon

  const openMenu = event => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = () => {
    setAnchorEl(null)
  }

  const confirm = mode => () => {
    closeMenu()
    setConfirming(mode)
  }

  const dismiss = () => {
    setConfirming('')
  }

  const promote = () => {
    props.promoteEmail(email)
    dismiss()
  }

  const remove = () => {
    props.removeEmail(email)
    dismiss()
  }

  return (
    <Fragment>
      <Stack
        className='email-address'
        component='div'
        sx={{
          alignItems: {sm: 'center'},
          bgcolor: 'Background',
          flexDirection: {xs: 'column', sm: 'row'},
          justifyContent: 'space-between'
        }}>
        <Stack className='summary' component='div' sx={{flexDirection: 'row'}}>
          <Stack className='icon' component='div' sx={{justifyContent: 'center'}}>
            <AddressIcon color={verified ? 'inherit' : 'warning'} fontSize='large' />
          </Stack>
          <Stack className='details' component='div'>
            <Typography className='address' variant='body1'>{email.address}</Typography>
            <Typography className='content' variant='body2'>{emailDetail(email)}</Typography>
          </Stack>
        </Stack>
        <Stack className='controls' component='div' direction='row'>
          {chip && <Chip className='status' size='small' label={chip.label} color={chip.color} />}
          {
            action && (
              <Button disabled={props.loading} onClick={confirm(action.mode)} size='small'>{action.label}</Button>
            )
          }
          <IconButton aria-label={`More options for ${email.address}`} onClick={openMenu} size='small'>
            <MoreIcon fontSize='small' />
          </IconButton>
        </Stack>
      </Stack>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
        <MenuItem disabled={isRemovalRefused(email)} onClick={confirm(REMOVE)}>
          <ListItemIcon>
            <DeleteIcon fontSize='small' />
          </ListItemIcon>
          Remove
        </MenuItem>
      </Menu>
      <Modal
        maxWidth='sm'
        open={confirming === REMOVE}
        handleClose={dismiss}
        buttonText='Close'
        title={`Remove ${email.address}?`}>
        <Typography gutterBottom>{prompt.body}</Typography>
        {prompt.warning && <Typography color='error' gutterBottom>{prompt.warning}</Typography>}
        <ButtonGroup direction='row'>
          <Button onClick={dismiss}>Keep it</Button>
          <Button color='error' loading={props.loading} onClick={remove}>Remove</Button>
        </ButtonGroup>
      </Modal>
      <Modal
        maxWidth='sm'
        open={confirming === PROMOTE}
        handleClose={dismiss}
        buttonText='Close'
        title={`Make ${email.address} your primary address?`}>
        <Typography gutterBottom>{promotion.body}</Typography>
        {promotion.detail && <Typography gutterBottom>{promotion.detail}</Typography>}
        <ButtonGroup direction='row'>
          <Button onClick={dismiss}>{promotion.keep}</Button>
          <Button color='primary' loading={props.loading} onClick={promote}>Make primary</Button>
        </ButtonGroup>
      </Modal>
      <VerifyEmail
        email={email}
        open={confirming === VERIFY}
        handleClose={dismiss}
        verifyEmail={props.verifyEmail}
        loading={props.loading}
      />
    </Fragment>
  )
}

EmailAddress.propTypes = {
  email: PropTypes.object.isRequired,
  emails: PropTypes.array.isRequired,
  promoteEmail: PropTypes.func.isRequired,
  removeEmail: PropTypes.func.isRequired,
  verifyEmail: PropTypes.func.isRequired,
  loading: PropTypes.bool
}

export default EmailAddress
